import React from 'react'
// libs
import { useLazyQuery } from '@apollo/client/react'
// graphql
import { INJURY_MASTER_RECORDS_BY_URL_QUERY } from 'graphql/injuryMaster/queries'

/**
 * Resolves a fixed set of Injury Master record URLs to their names — url -> name, missing an entry
 * until it resolves (or forever, if the record no longer exists). There is no lookup-by-url on the
 * list endpoints, so this is what lets the mapping UI show a saved choice's real name instead of its
 * raw URL. `urls` is read once (its first value) — a mapping row doesn't need to re-resolve as the
 * person edits it, only to show what was already saved.
 */
export const useInjuryMasterRecordNames = (urls: string[]): Record<string, string> => {
  const [names, setNames] = React.useState<Record<string, string>>({})

  const [fetchNames] = useLazyQuery<{ injuryMasterRecordsByUrl: { url: string; name: string }[] }>(
    INJURY_MASTER_RECORDS_BY_URL_QUERY,
    {
      fetchPolicy: 'network-only'
    }
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialUrls = React.useMemo(() => urls, [])

  React.useEffect(() => {
    if (!initialUrls.length) {
      return
    }

    fetchNames({ variables: { urls: initialUrls } }).then(({ data }) => {
      const resolved = data?.injuryMasterRecordsByUrl ?? []

      setNames(Object.fromEntries(resolved.map(({ url, name }) => [url, name])))
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return names
}
